
import { useState } from 'react';
import { OnboardingData } from '@/types/onboarding';
import { useSaveOnboardingStep } from './useSaveOnboardingStep';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';

interface StepValidationOptions {
  stepNumber: number;
  onSuccess?: () => void;
  onError?: (error: string, errorDetails?: any) => void;
  customValidation?: (data: OnboardingData) => { isValid: boolean; error?: string };
  showSuccessToast?: boolean; // Control when to show success toast
}

export const useStepValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const { saveStepData, saving } = useSaveOnboardingStep();

  const validateAndSaveStep = async (
    data: OnboardingData,
    options: StepValidationOptions
  ): Promise<boolean> => {
    const { stepNumber, onSuccess, onError, customValidation, showSuccessToast = false } = options;
    
    setIsValidating(true);
    
    try {
      // Executar validação customizada se fornecida
      if (customValidation) {
        const validation = customValidation(data);
        if (!validation.isValid) {
          const errorMessage = validation.error || 'Dados inválidos';
          
          // Create safe error details
          const safeErrorDetails = {
            message: errorMessage,
            timestamp: new Date().toISOString(),
            step: stepNumber,
            type: 'validation_failed'
          };

          onError?.(errorMessage, safeErrorDetails);
          toast.error(errorMessage);
          return false;
        }
      }

      debugLogger.debug(`Validating and saving step ${stepNumber}:`, data);
      
      // Tentar salvar os dados
      const success = await saveStepData(stepNumber, data);
      
      if (success) {
        debugLogger.debug(`Step ${stepNumber} saved successfully`);
        
        // Only show success toast when explicitly requested (for final steps or critical actions)
        if (showSuccessToast) {
          toast.success('Dados salvos com sucesso!');
        }
        
        onSuccess?.();
        return true;
      } else {
        const errorMessage = 'Erro ao salvar dados. Tente novamente.';
        debugLogger.error(`Failed to save step ${stepNumber}`);
        
        // Create safe error details for save failure
        const safeErrorDetails = {
          message: errorMessage,
          timestamp: new Date().toISOString(),
          step: stepNumber,
          type: 'save_failed'
        };

        onError?.(errorMessage, safeErrorDetails);
        showGracefulError(stepNumber, 'save_failed');
        return false;
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Erro inesperado ao salvar dados';
      debugLogger.error(`Exception saving step ${stepNumber}:`, error);
      
      // Create safe error details for exceptions
      const safeErrorDetails = {
        message: errorMessage,
        timestamp: new Date().toISOString(),
        step: stepNumber,
        type: 'exception'
      };

      onError?.(errorMessage, safeErrorDetails);
      showGracefulError(stepNumber, 'exception', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const showGracefulError = (stepNumber: number, errorType: string, error?: any) => {
    const errorDetails = {
      step: stepNumber,
      type: errorType,
      timestamp: new Date().toISOString(),
      error: error?.message || 'Unknown error'
    };

    debugLogger.error('Graceful error shown:', errorDetails);

    toast.error(
      'Erro ao salvar dados do onboarding',
      {
        description: 'Não foi possível salvar seus dados. Por favor, informe aos administradores do site sobre este problema.',
        action: {
          label: 'Reportar Problema',
          onClick: () => {
            // Copiar detalhes do erro para a área de transferência
            navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
            toast.success('Detalhes do erro copiados para a área de transferência');
          }
        },
        duration: 10000
      }
    );
  };

  return {
    validateAndSaveStep,
    isValidating: isValidating || saving
  };
};
